import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import ProgressBar from '../components/ProgressBar';
import { useAppState } from '../state/AppState';
import { useDataTransformation } from '../hooks/useDataTransformation';

// Age groups will be created dynamically from availability data

/**
 * ScreenK - Age group selection screen.
 *
 * Allows the user to select the age group of the person to be seen.
 */
const ScreenK: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch, state } = useAppState();
  const { ageGroups, hasAvailabilityData } = useDataTransformation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAgeGroups = async () => {
      try {
        if (hasAvailabilityData && ageGroups.length > 0) {
          setLoading(false);
          return;
        }
        
        // Fallback to static data if no availability data
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(loadAgeGroups, 500);
    
    return () => clearTimeout(timeoutId);
  }, [hasAvailabilityData, ageGroups.length]);

  const handleSelect = (id: string) => {
    dispatch({ type: 'SET_AGE_GROUP', payload: id });
    setTimeout(() => navigate('/e'), 150); // slight delay for button feedback
  };

  if (loading) {
    return (
      <div className="screen-bg">
        <div className="main-content-container">
          <div className="mtg-back-btn-align">
            <BackButton onClick={() => navigate(-1)} />
          </div>
          <main className="mtg-screen-main" id="main-content">
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 16px auto' }} />
              <div style={{ color: '#b5b5b5', fontWeight: 500 }} role="status" aria-live="polite">Loading age groups…</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (ageGroups.length === 0) {
    return (
      <div className="screen-bg">
        <div className="main-content-container">
          <div className="mtg-back-btn-align">
            <BackButton onClick={() => navigate(-1)} />
          </div>
          <main className="mtg-screen-main" id="main-content">
            <div className="mtg-screen-card mb-18">
              <h2 className="mtg-screen-question mb-24">
                Age of person to be seen.
              </h2>
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <div style={{ color: '#b5b5b5', fontWeight: 500 }} role="status" aria-live="polite">
                  No age groups available for the selected region.
                </div>
              </div>
              <ProgressBar progress={63}/>
            </div>
            <button
              className="mtg-secondary-action-btn mb-18"
              onClick={() => navigate('/v')}
              aria-label="My option is not listed, contact us"
            >
              MY OPTION IS NOT LISTED
            </button>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          <div className="mtg-screen-card mb-18">
            <h2 className="mtg-screen-question mb-24">
              Age of person to be seen.
            </h2>
            <div role="listbox" aria-label="Age group selection" className="button-container">
              {ageGroups.map((group) => (
                <button
                  key={group.id}
                  className="pill-btn-standard mb-24"
                  onClick={() => handleSelect(group.id)}
                  role="option"
                  aria-selected="false"
                  aria-label={`Select ${group.label} age group (${group.range})`}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div>{group.label}</div>
                    <div style={{ fontSize: '0.98em', fontWeight: 400, opacity: 0.85 }}>({group.range})</div>
                  </div>
                </button>
              ))}
            </div>
            <ProgressBar progress={63}/>
          </div>
          <button
            className="mtg-secondary-action-btn mb-18"
            onClick={() => navigate('/v')}
            aria-label="My option is not listed, contact us"
          >
            MY OPTION IS NOT LISTED
          </button>
        </main>
      </div>
    </div>
  );
};

export default ScreenK; 